import { FC, MutableRefObject, useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { KeenSliderInstance, KeenSliderPlugin, useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { useMedia } from 'react-use';

const ThumbnailPlugin = (
  mainRef: MutableRefObject<KeenSliderInstance | null>
): KeenSliderPlugin => {
  return slider => {
    function removeActive() {
      slider.slides.forEach(slide => {
        slide.classList.remove('active');
      });
    }
    function addActive(idx: number) {
      slider.slides[idx].classList.add('active');
    }

    function addClickEvents() {
      slider.slides.forEach((slide, idx) => {
        slide.addEventListener('click', () => {
          if (mainRef.current) mainRef.current.moveToIdx(idx);
        });
      });
    }

    slider.on('created', () => {
      if (!mainRef.current) return;
      addActive(slider.track.details.rel);
      addClickEvents();
      mainRef.current.on('animationStarted', main => {
        removeActive();
        const next = main.animator.targetIdx || 0;
        addActive(main.track.absToRel(next));
        slider.moveToIdx(Math.min(slider.track.details.maxIdx, next));
      });
    });
  };
};

type Slide = {
  title: string;
  description: string;
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  icon: {
    src: string;
    alt: string;
  };
};

const SlideGallery: FC<{ featureList: Slide[] }> = ({ featureList = [] }) => {
  const isMobile = useMedia('(max-width: 767px)');
  const [showCurtain, setShowCurtain] = useState({ left: false, right: true });
  const [loaded, setLoaded] = useState(false);
  const [cursor, setCursor] = useState<'grab' | 'grabbing'>('grab');
  const [thumbnailCursor, setThumbnailCursor] = useState<'grab' | 'grabbing'>('grab');
  const [details, setDetails] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, slider] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1,
      spacing: 32
    },
    created: () => setLoaded(true),
    dragStarted: () => setCursor('grabbing'),
    dragEnded: () => setCursor('grab'),
    detailsChanged: s => setDetails(s.track.details),
    slideChanged: s => setCurrentSlide(s.track.details.rel)
  });
  const [thumbnailRef] = useKeenSlider(
    {
      initial: 0,
      mode: 'snap',
      slides: {
        perView: 1,
        spacing: 24
      },
      breakpoints: {
        '(min-width: 768px)': {
          slides: {
            origin: 'center',
            perView: 1.4,
            spacing: 24
          }
        },
        '(min-width: 1024px)': {
          slides: {
            perView: 2.8,
            spacing: 24
          }
        },
        '(min-width: 1280px)': {
          mode: 'free',
          slides: {
            perView: 3,
            spacing: 32
          }
        }
      },
      dragStarted: () => setThumbnailCursor('grabbing'),
      dragged: s => {
        setShowCurtain({
          left: s.track.details.position > s.track.details.min,
          right: s.track.details.position < s.track.details.max
        });
      },
      dragEnded: s => {
        if (isMobile) {
          const next = s.animator.targetIdx || 0;
          slider.current.moveToIdx(Math.min(s.track.details.maxIdx, next));
        }
        setThumbnailCursor('grab');
        setShowCurtain({
          left: s.track.details.position > s.track.details.min,
          right: s.track.details.position < s.track.details.max
        });
      },
      animationStarted: s => {
        setShowCurtain({
          left: s.track.details.position > s.track.details.min,
          right: s.track.details.position < s.track.details.max
        });
      },
      animationEnded: s => {
        setShowCurtain({
          left: s.track.details.position > s.track.details.min,
          right: s.track.details.position < s.track.details.max
        });
      }
    },
    [ThumbnailPlugin(slider)]
  );

  const scaleStyle = idx => {
    if (!details) return {};
    const slide = details.slides[idx];
    const scale_size = 0.2;
    const scale = 1 - (scale_size - scale_size * slide.portion);

    return {
      transform: `scale(${scale})`,
      WebkitTransform: `scale(${scale})`
    };
  };

  return (
    <>
      <div className="relative flex items-center justify-center w-full px-6 py-12 mb-12 overflow-hidden rounded-2xl 3xl:aspect-video lg:mb-10 3xl:mb-16 sm:px-8 md:px-12">
        <Image
          className="absolute object-cover w-full h-full pointer-events-none select-none rounded-2xl"
          src="/static/images/components/ui/slide-gallery/gallery-bg.png"
          alt="Gallery background"
          width={1216}
          height={684}
          quality={100}
          unoptimized
        />
        <div ref={sliderRef} className="keen-slider !overflow-visible" style={{ cursor }}>
          {featureList.map((feature, index) => (
            <div key={index} className="keen-slider__slide">
              <Image
                className="mx-auto"
                style={scaleStyle(index)}
                src={feature.image.src}
                alt={feature.image.alt}
                width={feature.image.width}
                height={feature.image.height}
                quality={100}
                unoptimized
              />
            </div>
          ))}
        </div>
      </div>

      <div className="relative">
        {currentSlide !== 0 && (
          <button
            onClick={() => slider.current?.prev()}
            className="absolute z-20 items-center justify-start hidden w-6 h-full -translate-y-1/2 lg:flex -left-10 top-1/2 animate-fadeIn focus:outline-none focus-visible:outline-none"
          >
            <Image
              className="rotate-180"
              src="/static/images/global/icons/chevron-right.svg"
              alt="Next"
              width={24}
              height={24}
              quality={100}
            />
          </button>
        )}
        {currentSlide !== featureList.length - 1 && (
          <button
            onClick={() => slider.current?.next()}
            className="absolute z-20 items-center justify-end hidden w-6 h-full -translate-y-1/2 lg:flex -right-10 top-1/2 animate-fadeIn focus:outline-none focus-visible:outline-none"
          >
            <Image
              src="/static/images/global/icons/chevron-right.svg"
              alt="Next"
              width={24}
              height={24}
            />
          </button>
        )}
        <div className="overflow-hidden sm:overflow-visible lg:overflow-hidden !w-screen left-1/2 -translate-x-1/2 sm:!w-auto relative px-7 sm:px-0">
          <div
            ref={thumbnailRef}
            className="keen-slider thumbnail !overflow-visible"
            style={{ cursor: thumbnailCursor }}
          >
            {featureList.map((feature, index) => {
              const isSelected = currentSlide === index;

              return (
                <button
                  key={index}
                  className={cn(
                    isSelected && 'lg:bg-codGray',
                    'rounded-lg p-[1px] keen-slider__slide mb-24 lg:mb-0 lg:px-8 lg:py-10 text-left flex flex-col justify-start focus:outline-none focus-visible:outline-none'
                  )}
                >
                  <Image
                    src={feature.icon.src}
                    alt={feature.icon.alt}
                    className="mb-4 sm:mb-2 3xl:mb-4"
                    width={24}
                    height={24}
                    quality={100}
                  />
                  <p className="mb-4 text-xl sm:mb-2 3xl:mb-4 sm:text-lg 3xl:text-xl">
                    {feature.title}
                  </p>
                  <p className="text-base text-neutral-400 sm:text-sm 3xl:text-base">
                    {feature.description}
                  </p>
                </button>
              );
            })}
          </div>
          {loaded && slider.current && (
            <div className="absolute flex -mt-8 space-x-2 -translate-x-1/2 lg:hidden left-1/2">
              {[...Array(slider.current.track.details.slides.length).keys()].map(idx => {
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      slider.current?.moveToIdx(idx);
                    }}
                    className={cn(
                      currentSlide === idx ? 'bg-white' : 'bg-neutral-600',
                      'w-[10px] h-[10px] rounded-full focus:outline-none focus-visible:outline-none'
                    )}
                  ></button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SlideGallery;
